import React, { useEffect, useState } from 'react';
import { LOCALE_DISPLAY, SUPPORTED_LOCALES, SupportedLocale } from '@/i18n/index';
import { useLocale } from '@/contexts/LocaleContext';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { canSendClientAnalytics } from '@/lib/europeVisitor';

// Detect whether the current URL explicitly carries a supported locale prefix
// (e.g. /de-DE/pricing). Used to suppress the geo banner when a visitor lands
// on a locale-specific page — they likely came from a localized ad/SERP and
// suggesting they switch away would be annoying.
function getUrlLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/([a-z]{2}-[A-Z]{2})(\/|$)/);
  if (!m) return null;
  const candidate = m[1] as SupportedLocale;
  return (SUPPORTED_LOCALES as readonly string[]).includes(candidate) ? candidate : null;
}

// Country codes whose primary spoken language matches a supported locale family.
// Used for high-confidence auto-switch when IP country and browser language agree.
const COUNTRY_PRIMARY_LANG: Record<string, string> = {
  US: 'en', GB: 'en', IE: 'en', AU: 'en', NZ: 'en', SG: 'en', ZA: 'en',
  DE: 'de', AT: 'de', CH: 'de', LI: 'de',
  FR: 'fr', BE: 'fr', MC: 'fr',
  MX: 'es', ES: 'es', AR: 'es', CL: 'es', CO: 'es', PE: 'es',
  JP: 'ja',
};

// Maps navigator.language values to the closest supported locale.
// Goal: never strand an international visitor on /en-US/ when a closer locale exists.
// Strategy:
//  - Exact-region matches first
//  - Every English variant -> en-US. The site offers five languages, not
//    country markets, so Commonwealth, APAC and African English all land on
//    the one English locale rather than a flag that promised local pricing.
//  - DACH German (AT/CH/LI/LU) -> de-DE
//  - Francophone, Quebec included (BE/CH/LU/MC/MA/SN/CI/CA) -> fr-FR
//  - Iberian/LatAm Spanish (ES/AR/CL/CO/PE/etc.) -> es-MX
//  - LatAm/Iberian Portuguese -> es-MX (closest available)
//  - Japanese (any) -> ja-JP
const LANG_TO_LOCALE: Record<string, SupportedLocale> = {
  // English, every variant
  'en': 'en-US', 'en-US': 'en-US', 'en-CA': 'en-US', 'en-GB': 'en-US',
  'en-AU': 'en-US', 'en-NZ': 'en-US', 'en-IE': 'en-US', 'en-IN': 'en-US',
  'en-SG': 'en-US', 'en-HK': 'en-US', 'en-MY': 'en-US', 'en-PH': 'en-US',
  'en-ZA': 'en-US', 'en-NG': 'en-US', 'en-KE': 'en-US', 'en-GH': 'en-US',

  // German (DACH)
  'de-DE': 'de-DE', 'de': 'de-DE',
  'de-AT': 'de-DE', 'de-CH': 'de-DE', 'de-LI': 'de-DE', 'de-LU': 'de-DE',

  // French (France + Francophone Europe/Africa)
  'fr-FR': 'fr-FR', 'fr': 'fr-FR',
  'fr-BE': 'fr-FR', 'fr-CH': 'fr-FR', 'fr-LU': 'fr-FR', 'fr-MC': 'fr-FR',
  'fr-MA': 'fr-FR', 'fr-SN': 'fr-FR', 'fr-CI': 'fr-FR', 'fr-CA': 'fr-FR',

  // Spanish (Mexico is closest available; covers Iberian + LatAm)
  'es-MX': 'es-MX', 'es': 'es-MX',
  'es-ES': 'es-MX', 'es-AR': 'es-MX', 'es-CL': 'es-MX', 'es-CO': 'es-MX',
  'es-PE': 'es-MX', 'es-VE': 'es-MX', 'es-UY': 'es-MX', 'es-PY': 'es-MX',
  'es-BO': 'es-MX', 'es-EC': 'es-MX', 'es-GT': 'es-MX', 'es-CR': 'es-MX',
  'es-PA': 'es-MX', 'es-DO': 'es-MX', 'es-HN': 'es-MX', 'es-NI': 'es-MX',
  'es-SV': 'es-MX', 'es-PR': 'es-MX', 'es-US': 'es-MX',

  // Portuguese (closest available is es-MX as a LatAm fallback)
  'pt-BR': 'es-MX', 'pt-PT': 'es-MX', 'pt': 'es-MX',

  // Japanese
  'ja-JP': 'ja-JP', 'ja': 'ja-JP',
};

// Maps ISO 3166-1 alpha-2 country codes (from IP geolocation) to the closest supported locale.
// Drives the geo-fallback when navigator.language is misleading (e.g. Mumbai user with US English OS).
const COUNTRY_TO_LOCALE: Record<string, SupportedLocale> = {
  US: 'en-US',
  GB: 'en-US', IE: 'en-US', AU: 'en-US', NZ: 'en-US', IN: 'en-US',
  SG: 'en-US', HK: 'en-US', MY: 'en-US', PH: 'en-US', TH: 'en-US', ID: 'en-US', VN: 'en-US',
  ZA: 'en-US', NG: 'en-US', KE: 'en-US', GH: 'en-US', EG: 'en-US',
  DE: 'de-DE', AT: 'de-DE', CH: 'de-DE', LI: 'de-DE', LU: 'de-DE',
  FR: 'fr-FR', BE: 'fr-FR', MC: 'fr-FR', MA: 'fr-FR', SN: 'fr-FR', CI: 'fr-FR',
  CA: 'en-US',
  MX: 'es-MX', ES: 'es-MX', AR: 'es-MX', CL: 'es-MX', CO: 'es-MX', PE: 'es-MX',
  VE: 'es-MX', UY: 'es-MX', PY: 'es-MX', BO: 'es-MX', EC: 'es-MX', GT: 'es-MX',
  CR: 'es-MX', PA: 'es-MX', DO: 'es-MX', HN: 'es-MX', NI: 'es-MX', SV: 'es-MX', PR: 'es-MX',
  BR: 'es-MX', PT: 'es-MX',
  JP: 'ja-JP',
};

// Cloudflare's free trace endpoint returns plain text including `loc=XX`.
// No API key, no CORS issues, no rate limit for normal traffic.
async function fetchCountryFromIP(): Promise<string | null> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 1500);
    const res = await fetch('https://www.cloudflare.com/cdn-cgi/trace', {
      signal: controller.signal,
      cache: 'force-cache',
    });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const text = await res.text();
    const match = text.match(/^loc=([A-Z]{2})$/m);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

const GeoSuggestionBanner = () => {
  const [suggestedLocale, setSuggestedLocale] = useState<SupportedLocale | null>(null);

  let currentLocale: SupportedLocale = 'en-US';
  let setLocale: ((locale: SupportedLocale) => void) | null = null;

  try {
    const ctx = useLocale();
    currentLocale = ctx.currentLocale;
    setLocale = ctx.setLocale;
  } catch {
    return null;
  }

  useEffect(() => {
    if (localStorage.getItem('preferred-locale') || localStorage.getItem('geo-banner-dismissed')) {
      return;
    }

    // #3 Suppress on locale-specific landing pages. If the URL already carries
    // a supported locale prefix (e.g. /de-DE/), the visitor most likely arrived
    // from a localized ad/SERP — don't second-guess them.
    if (getUrlLocale()) {
      return;
    }

    let cancelled = false;

    const resolveSuggestion = async () => {
      // 1. Try navigator.language first (fast, no network)
      const browserLang = navigator.language;
      const langMatch = LANG_TO_LOCALE[browserLang] || LANG_TO_LOCALE[browserLang.split('-')[0]];

      // 2. IP-derived country (cached per session) — wins when present, since OS
      //    language is often US English even outside the US.
      let ipCountry = sessionStorage.getItem('geo-ip-country');
      if (!ipCountry) {
        const fetched = await fetchCountryFromIP();
        if (fetched) {
          ipCountry = fetched;
          sessionStorage.setItem('geo-ip-country', fetched);
        }
      }
      const ipMatch = ipCountry ? COUNTRY_TO_LOCALE[ipCountry] : undefined;

      const matched = ipMatch || langMatch;
      if (cancelled || !matched || matched === currentLocale) return;

      // #5 High-confidence auto-switch. If IP country and browser language
      // agree on the same primary language family, skip the banner and switch
      // silently — show an undo toast so the user can revert in one click.
      const browserPrimary = browserLang.split('-')[0].toLowerCase();
      const countryPrimary = ipCountry ? COUNTRY_PRIMARY_LANG[ipCountry] : undefined;
      const highConfidence =
        ipMatch && langMatch && ipMatch === langMatch && countryPrimary === browserPrimary;

      if (highConfidence) {
        // Mark as handled so we never re-prompt this visitor for this match.
        localStorage.setItem('geo-banner-dismissed', 'true');
        trackBanner('auto_switched', matched);
        // Fire-and-forget undo toast. Switching navigates, so show it first.
        toast(`Switched to ${LOCALE_DISPLAY[matched].language}`, {
          description: `Based on your browser language.`,
          action: {
            label: 'Undo',
            onClick: () => {
              trackBanner('auto_switch_undone', matched);
              setLocale!(currentLocale);
            },
          },
          duration: 8000,
        });
        setLocale!(matched);
        return;
      }

      setSuggestedLocale(matched);
    };

    resolveSuggestion();
    return () => { cancelled = true; };
  }, [currentLocale]);

  // Fire a GA4 event for banner interactions. Fire-and-forget; never throws.
  const trackBanner = (
    action: 'shown' | 'accepted' | 'dismissed' | 'auto_switched' | 'auto_switch_undone',
    targetLocale: SupportedLocale,
  ) => {
    try {
      const params = {
        action,
        from_locale: currentLocale,
        to_locale: targetLocale,
        ip_country: sessionStorage.getItem('geo-ip-country') || undefined,
        browser_lang: typeof navigator !== 'undefined' ? navigator.language : undefined,
      };
      const w = window as any;
      if (!canSendClientAnalytics()) return;
      if (typeof w.gtag === 'function') {
        w.gtag('event', 'geo_banner_' + action, params);
      }
    } catch { /* no-op */ }
  };

  // Fire 'shown' once per suggestion render.
  useEffect(() => {
    if (suggestedLocale) trackBanner('shown', suggestedLocale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedLocale]);

  if (!suggestedLocale || !setLocale) return null;

  const d = LOCALE_DISPLAY[suggestedLocale];

  const dismiss = () => {
    trackBanner('dismissed', suggestedLocale);
    localStorage.setItem('geo-banner-dismissed', 'true');
    setSuggestedLocale(null);
  };

  const accept = () => {
    trackBanner('accepted', suggestedLocale);
    setLocale!(suggestedLocale);
    localStorage.setItem('geo-banner-dismissed', 'true');
    setSuggestedLocale(null);
  };

  return (
    <div className="bg-accent text-accent-foreground text-sm py-2 px-4 flex items-center justify-center gap-3 relative">
      <span>
        {d.flag} It looks like you're in {d.country}. Switch to {d.language} ({d.currencySymbol})?
      </span>
      <button
        onClick={accept}
        className="font-semibold underline underline-offset-2 hover:opacity-80"
      >
        Switch
      </button>
      <button
        onClick={dismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-accent-foreground/60 hover:text-accent-foreground"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default GeoSuggestionBanner;
